import { streamReportingAiAsk } from '~/services/ReportingAiAskService';

import {
  type LensCardConfig,
  type LensCardType,
  type LensCardView,
  type LensColumnKind,
  type LensDataset,
  type LensDatasetColumn,
  type LensDatasetField,
  type LensFilter,
} from '~/types/leLens';

import { bestCardSql, describeRejection, fencedSqlBlocks, filterableColumns } from './buildPlan';
import { type Ask, type AskOptions, type BuildAnswer, newBuildSession } from './buildSession';
import { extractDatasetBinding, extractDatasetExtension, extractFieldBinding } from './cardDatasetBinding';
import { deriveCardTitle, extractFormatFromSql, extractTitleFromSql } from './cardTitle';
import type { Check } from './checkSql';
import { type TierDataset, type TierRefusal, checkTierProposal, extractTierDeclaration, proposeTier } from './lensTier';
import { configEntry, pickModelConfig } from './modelCardConfig';
import { type VerifiedSource, cardPrompt, replacementDatasetPrompt } from './prompts';
import { sourceTableName } from './workspaceCandidates';

export type RequestedCardType = LensCardType | 'auto';

const ABANDONED = 'The build was abandoned before it finished, so nothing more was written.';

/** Said to the reader when no query could be produced. Nothing was saved. */
const UNANSWERABLE =
  'Nothing was added. askRichard could not work out how to answer that from the data it can reach. Try naming the number you want and how to split it, like “revenue by month” or “orders by country”.';

/**
 * The four ways a card can be answered, cheapest first. 'view' and 'field' add
 * no query. 'extend' rewrites a dataset's own query and is guarded before it
 * is applied (the extend-check endpoint). 'dataset' is a query nothing on the
 * board could avoid, turned into a first-class one-card dataset rather than a
 * card that owns its own SQL: 00_PLAN.md's contract has no shape for the
 * latter any more, and a card that cannot bind anywhere is exactly the case a
 * fresh dataset exists to hold.
 */
export const LENS_CARD_TIERS = ['view', 'field', 'extend', 'dataset'] as const;

export type LensCardTier = (typeof LENS_CARD_TIERS)[number];

/** Which tier answered the card, why, and the one-line decision the plan gate
 *  shows for it (01_USER_FLOWS.md F3). */
export type GeneratedCardTier = { tier: LensCardTier; reason: string; decision: string };

export type GeneratedLensCard = {
  id: string;
  title: string;
  description: string;
  type: LensCardType;
  insightQuestion?: string;
  config?: LensCardConfig;
  /** Set for every non-insight card: which dataset it reads. */
  datasetId?: string;
  view?: LensCardView;
  /** The tier this card was answered at, as the model stated it. */
  tier?: GeneratedCardTier;
};

// A person should describe the question, not have to know the rendering
// taxonomy. This conservative local choice only picks the initial card frame;
// AskRichard's generated SQL and the editor remain the source of truth.
export function inferRequestedCardType(request: string): LensCardType {
  const text = request.toLocaleLowerCase();
  if (/\b(drivers?|driving|contribution)\b/.test(text)) return 'drivers';
  if (/\b(why|explain|attention|watch.?out)\b/.test(text)) return 'insight';
  if (/\b(over time|by day|daily|weekly|monthly|trend|history)\b/.test(text)) return 'line';
  if (/\b(by |split|breakdown|compare|top \d+)\b/.test(text)) return 'bar';
  if (/\b(table|list|detail|rows)\b/.test(text)) return 'table';
  return 'metric';
}

export function makeUniqueCardId(request: string, existingIds: Array<string>): string {
  const base =
    request
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .replace(/^([^a-z])/, 'card-$1')
      .slice(0, 58) || 'card';
  const ids = new Set(existingIds);
  if (!ids.has(base)) {
    return base;
  }
  let counter = 2;
  while (ids.has(`${base}-${counter}`)) {
    counter += 1;
  }
  return `${base}-${counter}`;
}

const MAX_DATASET_ID_LENGTH = 64;
const MAX_DATASET_NAME_LENGTH = 120;

/** Matches the migration's own naming (srp-dataset-first normalise.ts,
 *  oneCardDatasetName): a reader who has seen one of these on an upgraded
 *  dashboard recognises the other kind by the same name. */
function oneCardDatasetName(cardTitle: string): string {
  const name = `Card query: ${cardTitle}`;
  return name.length <= MAX_DATASET_NAME_LENGTH ? name : name.slice(0, MAX_DATASET_NAME_LENGTH);
}

/**
 * Mints the one-card dataset a query nothing on the board could avoid becomes: named and id'd the
 * same way this module's own tier-'dataset' rung below does, so a reader who has seen one of these
 * recognises the other by the same name whichever path minted it. Exported for
 * `PlanGateDialog.tsx`'s F4 refusal escalation ("Create a new dataset for this instead"), which
 * needs the identical shape for a candidate SQL an extend-check has already refused, not a second
 * naming scheme.
 */
export function mintOneCardDataset({
  cardIdBase,
  datasetNameBase,
  existingIds,
  grain,
  sql,
  title,
}: {
  /** What the card's own id is minted from, e.g. the request text that produced it. */
  cardIdBase: string;
  /** What the dataset's own id is minted from, e.g. the card's title. */
  datasetNameBase: string;
  existingIds: ReadonlyArray<string>;
  grain?: Array<string>;
  sql: string;
  title: string;
}): { cardId: string; dataset: LensDataset } {
  const taken = new Set(existingIds);
  const cardId = makeUniqueCardId(cardIdBase, [...taken]);
  taken.add(cardId);
  const datasetId = uniqueDatasetId(datasetNameBase, taken);
  return {
    cardId,
    dataset: {
      id: datasetId,
      name: oneCardDatasetName(title),
      sql,
      source: { kind: 'sql' },
      ...(grain?.length ? { grain: { keys: grain } } : {}),
    },
  };
}

/** A dataset id, unique against every id already taken. Dataset ids share one
 *  id space with cards on a Lens spec, so `taken` must include both. */
function uniqueDatasetId(source: string, taken: ReadonlySet<string>): string {
  const base =
    source
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, MAX_DATASET_ID_LENGTH) || 'dataset';
  if (!taken.has(base)) {
    return base;
  }
  let suffix = 2;
  while (taken.has(`${base}-${suffix}`)) {
    suffix += 1;
  }
  return `${base}-${suffix}`;
}

/**
 * The view a freshly minted one-card dataset gets, with no dry run yet to
 * type its columns from.
 *
 * Ported from srp-dataset-first's normalise.ts (`deriveCardView`'s no-columns
 * branch), which every other upgrade path already produces: a version 2 view
 * can never be empty, and `{ mode: 'grouped' }` is the same no-op cut a card
 * with no view at all always rendered. The panel or a later dry run replaces
 * it with a real cut once the dataset's columns are known; this only has to
 * be valid, not right.
 */
function placeholderCardView(cardType: LensCardType): LensCardView {
  if (cardType === 'insight') {
    return {};
  }
  if (cardType === 'metric') {
    return { aggregate: 'sum' };
  }
  return { mode: 'grouped' };
}

const GRAIN_COMMENT = /^\s*--\s*grain:\s*(.+?)\s*$/im;

/** The grain a model declared for a rewritten or brand-new dataset's SQL,
 *  split on commas. Absent when it said nothing, which the caller records as
 *  an undeclared grain rather than inventing keys to check uniqueness with. */
function extractGrainDeclaration(text: string): Array<string> | undefined {
  const declared = text.match(GRAIN_COMMENT)?.[1];
  if (!declared) {
    return undefined;
  }
  const keys = declared
    .split(',')
    .map((key) => key.trim())
    .filter(Boolean);
  return keys.length ? keys : undefined;
}

/**
 * A change to a dataset that adding this card implies.
 *
 * Returned rather than applied, because the dataset belongs to the board and
 * not to the card: the caller is the one holding the spec, and it saves the
 * dataset and the card together or neither. A brand new dataset is not one of
 * these: it is returned separately (`dataset` on the result below), since
 * there is no existing dataset in the caller's array for a kind here to
 * modify.
 */
export type DatasetChange =
  | { kind: 'field'; datasetId: string; field: LensDatasetField }
  | { kind: 'extend'; datasetId: string; sql: string; grain?: Array<string> };

export type ReplacementDatasetBinding = {
  datasetId: string;
  view: LensCardView;
  change?: DatasetChange;
};

/**
 * Gives a chat answer one chance to remain on the dataset that its replacement
 * target already uses. The returned candidate is validated against the
 * dataset's declared columns, so a confident but incompatible answer simply
 * falls back to its own query instead of changing every card sharing the
 * dataset.
 */
export async function reconcileReplacementWithDataset({
  title,
  type,
  sql,
  dataset,
  filters = [],
  onStatus,
}: {
  title: string;
  type: LensCardType;
  sql: string;
  dataset: LensDataset;
  filters?: Array<LensFilter>;
  onStatus: (message: string | null) => void;
}): Promise<ReplacementDatasetBinding | undefined> {
  const columns = [
    ...(dataset.columns ?? []).map((column) => column.name),
    ...(dataset.fields ?? []).map((field) => field.name),
  ];
  if (!columns.length) {
    return undefined;
  }

  onStatus('Checking whether this can stay on the shared dataset…');
  const response = await runCardStream(
    replacementDatasetPrompt({
      title,
      type,
      candidateSql: sql,
      dataset: { id: dataset.id, name: dataset.name, columns, sql: dataset.sql },
      filterColumns: filterableColumns(filters),
    }),
    onStatus,
  );
  if (response.streamError) {
    return undefined;
  }

  const available = [{ id: dataset.id, columns }];
  const bound = extractDatasetBinding(response.answer, available);
  if (bound) {
    return bound;
  }
  const field = extractFieldBinding(response.answer, available);
  if (field) {
    return {
      datasetId: field.datasetId,
      view: field.view,
      change: { kind: 'field', datasetId: field.datasetId, field: field.field },
    };
  }
  const extension = extractDatasetExtension(response.answer, response.sqlBlocks, available);
  if (extension) {
    const grain = extractGrainDeclaration(response.answer);
    return {
      datasetId: extension.datasetId,
      view: extension.view,
      change: { kind: 'extend', datasetId: extension.datasetId, sql: extension.sql, ...(grain ? { grain } : {}) },
    };
  }
  return undefined;
}

/**
 * One AskRichard turn for a card query. Every sql block it streamed is kept,
 * not just the last one: the model is asked to check its work before
 * answering, so the last block is as often the check as the card.
 */
export async function runCardStream(
  question: string,
  // Optional: LensCardDialog.tsx carried its own copy of this function with
  // no progress callback at all (hardcoded to a no-op) before its deletion
  // (lane C4) consolidated every caller onto this one. Defaulted rather than
  // made required, so streamAiSql.ts's 1-argument re-export (AddColumn.tsx,
  // NewDataset.tsx, and their tests) keeps compiling unchanged.
  onProgress: (message: string | null) => void = () => {},
  options?: AskOptions,
): Promise<BuildAnswer> {
  const session = options?.session;
  let answer = '';
  const sqlBlocks: Array<string> = [];
  let streamError = '';
  let usage: BuildAnswer['usage'];
  await streamReportingAiAsk(
    {
      question,
      ...(session?.id ? { sessionId: session.id } : {}),
      ...(options?.effort ? { effort: options.effort } : {}),
      origin: 'lens_build',
      humanInitiated: false,
    },
    {
      onProgress: (message) => onProgress(message ?? 'Working on it…'),
      onSession: (sessionId) => {
        if (session) {
          session.id = sessionId;
        }
      },
      onContent: (block) => {
        if (block.type === 'sql') {
          const candidate = block.payload?.content;
          if (typeof candidate === 'string' && candidate.trim() && !sqlBlocks.includes(candidate.trim())) {
            sqlBlocks.push(candidate.trim());
          }
          return;
        }
        const part = block.payload?.content;
        if (typeof part === 'string') {
          answer += `\n${part}`;
        }
      },
      onDone: (detail) => {
        if (session && detail?.sessionId) {
          session.id = detail.sessionId;
        }
        if (detail?.usage) {
          usage = detail.usage;
        }
      },
      onStreamError: (message) => {
        streamError = message;
      },
    },
    { ...(options?.signal ? { signal: options.signal } : {}) },
  );
  // Thrown rather than answered empty: an abandoned build must stop its chain, not save a
  // dashboard of the cards it happened to finish first.
  if (options?.signal?.aborted) {
    throw new Error(ABANDONED);
  }
  const candidates = [...sqlBlocks, ...fencedSqlBlocks(answer).filter((block) => !sqlBlocks.includes(block))];
  return { sqlBlocks: candidates, answer, streamError, ...(usage ? { usage } : {}) };
}

/** "reporting.sales" reads well in "New dataset from reporting.sales."; a
 *  subquery or an undeclared source does not, so the sentence drops the
 *  clause instead of reading like a sentence fragment. */
function newDatasetSourceClause(sql: string): string {
  const table = sourceTableName(sql);
  return /\s/.test(table) ? '' : ` from ${table}`;
}

/** A dataset's column-name-to-kind map, for the tier router (lensTier.ts) to prefer over its own
 *  name-based guess. Built from the same declared columns `dataset.columns` already carries once
 *  a dry run has typed them; a dataset nobody has run yet passes none, and the router falls back
 *  to its regex entirely, exactly as it did before declared kinds existed. */
function kindsByColumnName(
  declaredColumns: ReadonlyArray<LensDatasetColumn> | undefined,
): Record<string, LensColumnKind> | undefined {
  if (!declaredColumns?.length) {
    return undefined;
  }
  return Object.fromEntries(declaredColumns.map((column) => [column.name, column.kind]));
}

/**
 * Turns a plain-language request into a card, or into the reason it could not
 * be one. Shared by every surface that adds a card, so the dialog and the
 * in-place draft cannot drift on retries, titling or id collisions.
 *
 * Never returns a card carrying its own query: the four rungs below are
 * `view`, `field`, `extend` and `dataset`, and the last one still produces a
 * card with `datasetId` and `view`, reading a brand new dataset returned
 * alongside it. Routing this way, against the dataset's declared columns
 * before any SQL is trusted, is what stops the board silently growing a
 * second, disagreeing query for a number a dataset already answers.
 */
export async function generateLensCard({
  request,
  type,
  filters = [],
  datasets = [],
  existingIds,
  onStatus,
  ask,
  sources,
  check,
}: {
  request: string;
  type: RequestedCardType;
  filters?: Array<LensFilter>;
  /** Datasets already on the Lens, with the columns they actually return. `declaredColumns`
   *  carries each column's dry-run kind, when the dataset has one, so the tier router can prefer
   *  it over its own name-based guess; a dataset with none of these falls back to that guess
   *  entirely, same as before this existed. */
  datasets?: Array<{
    id: string;
    name: string;
    columns: Array<string>;
    declaredColumns?: Array<LensDatasetColumn>;
    reads?: string;
  }>;
  existingIds: Array<string>;
  onStatus: (message: string | null) => void;
  /** Supplied when several cards are written at once, so each has its own session. */
  ask?: Ask;
  /** Tables already looked up for this dashboard, handed over instead of found again. */
  sources?: ReadonlyArray<VerifiedSource>;
  /** Supplied when the query is checked against the warehouse rather than by the model itself. */
  check?: Check;
}): Promise<{
  card?: GeneratedLensCard;
  change?: DatasetChange;
  /** A brand new one-card dataset, present only for a tier 'dataset' card.
   *  Not routed through `change`: there is nothing existing for a change to
   *  modify, so the caller adds this to its dataset array directly. */
  dataset?: LensDataset;
  error?: string;
  refusal?: TierRefusal;
}> {
  const cardRequest = request.trim();
  if (!cardRequest) {
    return { error: 'Say what you want to see, for example “revenue by month”.' };
  }

  const resolvedType = type === 'auto' ? inferRequestedCardType(cardRequest) : type;

  if (resolvedType === 'insight') {
    return {
      card: {
        id: makeUniqueCardId(cardRequest, existingIds),
        title: deriveCardTitle(cardRequest),
        description: cardRequest,
        type: resolvedType,
        insightQuestion: cardRequest,
      },
    };
  }

  onStatus('Looking for the data…');

  const filterColumns = filterableColumns(filters);
  // Worked out before the model is asked, so the prompt can be primed with the
  // columns that already exist and the answer can be held to the same standard.
  const tierDatasets: Array<TierDataset> = datasets.map((dataset) => ({
    id: dataset.id,
    name: dataset.name,
    columns: dataset.columns,
    kinds: kindsByColumnName(dataset.declaredColumns),
    reads: dataset.reads,
  }));
  const proposal = proposeTier(cardRequest, tierDatasets);
  const own = newBuildSession();
  const askModel: Ask =
    ask ?? ((question, options) => runCardStream(question, onStatus, { ...options, session: options?.session ?? own }));
  const askForCard = (correction?: string) =>
    askModel(
      cardPrompt({
        type: resolvedType,
        request: cardRequest,
        filterColumns,
        datasets,
        tier: proposal,
        ...(sources?.length ? { sources } : {}),
        ...(correction ? { correction } : {}),
      }),
      { effort: 'low' },
    );

  const first = await askForCard();
  const sqlBlocks = first.sqlBlocks;
  let streamError = first.streamError;

  const datasetName = (datasetId: string): string =>
    datasets.find((entry) => entry.id === datasetId)?.name ?? datasetId;

  // A dataset already on the Lens is the better answer when it fits: the runner
  // runs each distinct query once, so the card costs nothing to add and cannot
  // disagree with the cards beside it. Checked against that dataset's real
  // columns before it is taken, so a confident but wrong reuse falls through to
  // the next rung rather than rendering a number that only looks right.
  const onDataset = (
    datasetId: string,
    view: LensCardView,
    tier: Exclude<LensCardTier, 'dataset'>,
    decision: string,
  ): GeneratedLensCard => ({
    id: makeUniqueCardId(cardRequest, existingIds),
    title: deriveCardTitle(cardRequest),
    description: cardRequest,
    type: resolvedType,
    datasetId,
    view,
    tier: { tier, reason: proposal.reason, decision },
  });

  /**
   * The three rungs that add no query of their own, in the order the prompt
   * asks for them.
   *
   * Read here rather than trusted: each is checked against the dataset's real
   * columns, and a calculated field's formula is parsed, so an answer that is
   * confident but wrong falls through to the next rung rather than rendering a
   * number that only looks right. An extend candidate is accepted as a
   * proposal only: whether the rewrite actually keeps every column and every
   * dependent card's number is the extend-check guard's job, run once the
   * reader confirms (01_USER_FLOWS.md F4), not this parser's.
   */
  const withoutAQuery = (
    text: string,
    blocks: ReadonlyArray<string>,
  ): { card: GeneratedLensCard; change?: DatasetChange } | undefined => {
    const bound = extractDatasetBinding(text, datasets);
    if (bound) {
      return {
        card: onDataset(bound.datasetId, bound.view, 'view', `Uses ${datasetName(bound.datasetId)}. No new query.`),
      };
    }

    const field = extractFieldBinding(text, datasets);
    if (field) {
      return {
        card: onDataset(
          field.datasetId,
          field.view,
          'field',
          `Adds ${field.field.name} to ${datasetName(field.datasetId)} as a calculated field. No new query.`,
        ),
        change: { kind: 'field', datasetId: field.datasetId, field: field.field },
      };
    }

    const extension = extractDatasetExtension(text, blocks, datasets);
    if (extension) {
      const existingColumns = datasets.find((entry) => entry.id === extension.datasetId)?.columns ?? [];
      const addedColumn = [extension.view.x, extension.view.series, extension.view.value].find(
        (column): column is string => Boolean(column) && !existingColumns.includes(column),
      );
      const name = datasetName(extension.datasetId);
      const decision = addedColumn
        ? `Extends ${name} to also return ${addedColumn}. One dry run, then every card reading it is re-checked.`
        : `Extends ${name} to return what this needs. One dry run, then every card reading it is re-checked.`;
      const grain = extractGrainDeclaration(text);
      return {
        card: onDataset(extension.datasetId, extension.view, 'extend', decision),
        change: { kind: 'extend', datasetId: extension.datasetId, sql: extension.sql, ...(grain ? { grain } : {}) },
      };
    }

    return undefined;
  };

  /**
   * Whether the answer tried a cheaper rung at all.
   *
   * An answer that bound a dataset, declared a field or rewrote a dataset's
   * query and had it rejected by the parse above is allowed its fallback rung:
   * the cheaper rung was reached for and did not fit. An answer that never
   * mentioned one went straight for the warehouse, which is the case the guard
   * exists to stop.
   */
  const attemptedCheaperRung = (text: string): boolean => /^\s*--\s*(dataset|field|extend):/im.test(text);

  const firstReuse = withoutAQuery(first.answer, sqlBlocks);
  if (firstReuse) {
    return firstReuse;
  }

  const rejectionOf = async (sql: string): Promise<string> => {
    if (!check || !sql) {
      return '';
    }
    const verdict = await check(sql);
    return verdict.ok
      ? ''
      : `The warehouse rejected that query: ${verdict.error ?? 'it did not run'}. Write it again, fixing exactly that.`;
  };

  let generatedSql = bestCardSql(sqlBlocks) ?? '';
  let generatedFrom = first.answer;
  let rejected = await rejectionOf(generatedSql);

  // One retry, carrying forward what was specifically wrong with the first
  // answer rather than asking the same question again unchanged.
  if (!streamError && (!generatedSql || rejected)) {
    onStatus('Trying a different way…');
    const retried = await askForCard(rejected || describeRejection(sqlBlocks, []));
    const retryReuse = withoutAQuery(retried.answer, retried.sqlBlocks);
    if (retryReuse) {
      return retryReuse;
    }
    const retrySql = bestCardSql(retried.sqlBlocks) ?? '';
    const retryRejected = await rejectionOf(retrySql);
    if (retrySql && !retryRejected) {
      generatedSql = retrySql;
      generatedFrom = retried.answer;
      rejected = '';
    } else if (!generatedSql) {
      streamError = retried.streamError;
      rejected = retryRejected;
    }
  }

  const declared = extractTierDeclaration(first.answer);
  const refusal = checkTierProposal({
    declared,
    proposal,
    attempted: attemptedCheaperRung(first.answer),
  });
  if (refusal) {
    return { error: refusal.message, refusal };
  }

  if (streamError || !generatedSql || rejected) {
    // describeRejection names the specific fault in each query, which is what
    // the retry above needs and not what a reader needs. A reader needs to know
    // that nothing was saved and what to change.
    return { error: streamError || UNANSWERABLE };
  }

  // Prefer the name and format AskRichard gave the card; derive a name only if
  // it did not, and leave the format unset rather than guessing at the scale.
  const named = extractTitleFromSql(generatedSql);
  const formatted = extractFormatFromSql(named.sql);
  const title = named.title || deriveCardTitle(cardRequest);

  // Nothing on the board could answer this: the query becomes a first-class
  // one-card dataset rather than a card that owns its own SQL, so a second
  // card asking for a cut of the same rows reads it too instead of paying for
  // a second, disagreeing scan (00_PLAN.md, "no fall-through to an own-SQL
  // card"). Card and dataset ids share one space, so the card's id is minted
  // and reserved before the dataset's.
  const grain = extractGrainDeclaration(generatedFrom) ?? extractGrainDeclaration(formatted.sql);
  const { cardId, dataset: newDataset } = mintOneCardDataset({
    cardIdBase: cardRequest,
    datasetNameBase: title,
    existingIds,
    grain,
    sql: formatted.sql,
    title,
  });

  return {
    card: {
      id: cardId,
      title,
      description: cardRequest,
      type: resolvedType,
      datasetId: newDataset.id,
      view: placeholderCardView(resolvedType),
      tier: {
        tier: 'dataset',
        reason: declared.reason ?? proposal.reason,
        decision: `New dataset${newDatasetSourceClause(formatted.sql)}. One query.`,
      },
      ...configEntry(pickModelConfig({ format: formatted.format })),
    },
    dataset: newDataset,
  };
}
